<template>
  <div>
    <div
      v-if="!requiredFieldsSet"
      class="d-flex justify-center"
    >
      <div class="pt-16">
        <v-alert
          border="left"
          max-width="600"
          outlined
          prominent
          text
          type="error"
        >
          In order to configure notifications the Date
          <span v-if="!(isNote || isTask)">, Start Time</span> and Timezone fields must be filled.
        </v-alert>
      </div>
    </div>
    <DocketEntryAttendeeNotifications
      v-else
      v-model="notifications"
      :docket-entry="docketEntry"
      :legal-case="legalCase"
      :notifications="originalNotifications"
      :parties="availableParties"
      :reschedule="isDocketEntryEditorInRescheduleMode"
      :valid.sync="isValid"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { RECORD_NAMESPACE } from '@/store/record'
import { allowedNotificationEvents } from '@/store/docket-entry/partials/docket-entry-edit'

import DocketEntryAttendeeNotifications from '@/components/DocketEntry/DocketEntryAttendeeNotifications.vue'

export default {
  name: 'DocketEntryEditorNotifications',

  components: {
    DocketEntryAttendeeNotifications,
  },

  data () {
    return {
      isValid: false,
      notifications: [],
      record: null,
      requiresAttention: false,
    }
  },

  computed: {
    availableParties () {
      let parties = this.docketEntry?.attendances?.reduce((res, att) => {
        const party = this.legalCaseParties.find(p => p.id === att.party_id)
        if (party) res.push(party)
        return res
      }, [])

      if (!parties.length) {
        parties = this.legalCaseParties.
          concat([
            { ...this.record?.record_holder_party, id: this.record?.record_holder_party_id },
            { ...this.record?.contact_party, id: this.record?.contact_party_id },
          ]).
          reduce((res, party) => {
            if (!res.find(p => p.id === party.id)) res.push(party)
            return res
          }, [])
      }

      return parties
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryType () {
      return this.docketEntryTypes.find(det => det.id === this.docketEntry.docket_entry_type_id)
    },

    isNote () {
      return this.docketEntry.action_type === 'Note'
    },

    isTask () {
      return this.docketEntry.action_type === 'Task'
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    originalNotifications () {
      return this.originalDocketEntry?.docket_entry_notifications ?? []
    },

    requiredFieldsSet () {
      const timeRequired = !(this.isNote || this.isTask)

      return this.docketEntryType &&
        this.availableParties.length &&
        this.docketEntry.action_date &&
        this.docketEntry.timezone &&
        (!timeRequired || this.docketEntry.event_time_start)
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
      'isDocketEntryEditorInRescheduleMode',
      'originalDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),
  },

  watch: {
    availableParties: {
      handler (newVal, oldVal) {
        if (!oldVal || newVal.length > oldVal?.length) this.requiresAttention = true
      },

      immediate: true,
    },

    isValid: {
      handler (value) {
        this.$emit('update:valid', value && this.requiredFieldsSet)
      },

      immediate: true,
    },

    legalCase: {
      handler (val) {
        if (!val) return
        if (val.record_id === this.record?.id) return

        this.getRecordById(val.record_id).then(data => { this.record = data })
      },

      immediate: true,
    },

    notifications () {
      this.buildDocketEntryNotificationsModel()
    },

    requiredFieldsSet: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        this.requiresAttention = !!this.notifications.length
      },

      immediate: true,
    },

    requiresAttention: {
      handler (val) { this.$emit('update:warning', val) },
      immediate: true,
    },
  },

  methods: {
    buildDocketEntryNotificationsModel () {
      const action = this.reschedule ? 'reschedule' : (this.docketEntry.id ? 'edit' : 'create')

      const docketEntryNotifications = this.notifications.reduce((res, item) => {
        const detn = this.docketEntryType?.
          docket_entry_type_notifications.
          find(detn => detn.id === item.docket_entry_type_notification_id)

        if (this.reschedule && allowedNotificationEvents(action).includes(detn.event_type)) return res
        if (this.reschedule && item._destroy) return res

        res.push({
          ...item,
          docket_entry_id: this.docketEntry.id,
          id: this.reschedule ? null : item.id,
          trigger_at: item.trigger_at || moment().format(),
        })

        return res
      }, [])

      this.docketEntry.docket_entry_notifications = docketEntryNotifications
    },

    onTabOpened () {
      if (this.requiredFieldsSet) this.requiresAttention = false
    },

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordById',
    ]),
  },
}
</script>

<style scoped>
.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
